import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PENDING_KEY,
  PENDING_TIMEOUT,
  POINTS,
  QuestAccent,
  TARGET_STREAK,
} from '../daily-quests-page.constants';
import {
  NextTimeStatus,
  QuestPeriod,
  QuestTypes,
  type IQuest,
  type UserQuestStruct,
} from '../daily-quests-page.types';
import {
  autoRequestKey,
  convertToMs,
  guestUser,
  statusFromNextTime,
  withPrizeCratePoints,
} from '../daily-quests-page.utils';
import { MOCKED_BONUS_POINTS_VALUE } from 'e2e-tests/mocks/mock-rooms';

import { writeRunDailyQuest } from 'modules/nfts/functions/write-functions';
import { getPoolBalance } from 'modules/prize-box-page/services/raffle-adapter';
import { formatUsd } from 'modules/prize-box-page/services/units';

import { ROUTES } from 'shared/constants';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useQuestsStore } from 'shared/store/questsStore';
import { isMocked } from 'shared/utils/isMocked';

export function useQuests() {
  const { t } = useTranslation();
  const { address, chainId } = useAccount();
  const walletClient = useWalletClient();
  const publicClient = usePublicClient();

  const {
    questsData,
    pointBalance,
    items,
    loading,
    error,
    setActiveQuests,
    setTotals,
    setLoading,
    setError,
    setItems,
    setClaim,
    setRefetch,
    setPoints,
  } = useQuestsStore();

  const [txPending, setTxPending] = useState<string | null>(null);

  const buildQuests = useCallback(
    (user: UserQuestStruct): IQuest[] => {
      const dailyLogin: IQuest = {
        id: QuestTypes.Daily,
        title: t('pages.dailyQuests.dailyLogIn'),
        subtitle: t('pages.dailyQuests.dailyLoginDesc'),
        description: '',
        frequency: QuestPeriod.Daily,
        points: POINTS.dailyLogin,
        status: statusFromNextTime(user.nextDailyQuestTime),
        nextAvailableAt: convertToMs(user.nextDailyQuestTime),
        accent: QuestAccent.Bronze,
      };

      const dailyPlay: IQuest = {
        id: QuestTypes.DailyPlay,
        title: t('pages.dailyQuests.dailyPlay'),
        subtitle: t('pages.dailyQuests.dailyPlayDesc'),
        description: t('pages.dailyQuests.dailyPlayHelp'),
        frequency: QuestPeriod.Daily,
        points: POINTS.dailyPlay,
        status: statusFromNextTime(user.nextDailyGameTime),
        nextAvailableAt: convertToMs(user.nextDailyGameTime),
        accent: QuestAccent.Silver,
      };

      const dailyWin: IQuest = {
        id: QuestTypes.DailyWin,
        title: t('pages.dailyQuests.dailyWin'),
        subtitle: t('pages.dailyQuests.dailyWinDesc'),
        description: t('pages.dailyQuests.dailyWinHelp'),
        frequency: QuestPeriod.Daily,
        points: POINTS.dailyWin,
        status: statusFromNextTime(user.nextDailyWinTime),
        nextAvailableAt: convertToMs(user.nextDailyWinTime),
        accent: QuestAccent.Gold,
      };

      const dailyPrizeCrate: IQuest = {
        id: QuestTypes.DailyPrizeCrate,
        title: t('pages.dailyQuests.dailyPrizeCrate'),
        subtitle: t('pages.dailyQuests.dailyPrizeCrateDesc'),
        description: '',
        frequency: QuestPeriod.Daily,
        points: 0, // Will be overwritten via pool sync effect
        status: null,
        nextAvailableAt: null,
        accent: QuestAccent.Rainbow,
        isPrizeCrate: true,
        href: ROUTES.PRIZE_BOX,
      };

      const streak = Number(user.weekStreak ?? 0);
      const weeklyStreak: IQuest = {
        id: QuestTypes.WeeklyStreak,
        title: t('pages.dailyQuests.weeklyStreak'),
        subtitle: t('pages.dailyQuests.weeklyStreakDesc'),
        description: '',
        frequency: QuestPeriod.Weekly,
        points: POINTS.weeklyStreak,
        status: streak >= TARGET_STREAK ? NextTimeStatus.Ready : NextTimeStatus.InProgress,
        nextAvailableAt: 0,
        progress: { current: Math.min(streak, TARGET_STREAK), target: TARGET_STREAK },
        accent: QuestAccent.Ice,
      };

      return [dailyLogin, dailyPlay, dailyWin, dailyPrizeCrate, weeklyStreak];
    },
    [t],
  );

  const refetch = useCallback(async () => {
    if (isMocked) {
      setItems(buildQuests(guestUser));
      setPoints(MOCKED_BONUS_POINTS_VALUE);
      setError(null);
      setLoading(false);
      return;
    }

    // If not ready, render guest state for now.
    if (!publicClient || !address || !questsData) {
      setItems(buildQuests(guestUser));
      setPoints(0n);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      setItems(buildQuests(questsData));
      setPoints(pointBalance);
    } catch (e) {
      console.error(e);
      setError('Failed to fetch quests');
      setItems(buildQuests(guestUser));
    } finally {
      setLoading(false);
    }
  }, [
    publicClient,
    address,
    questsData,
    pointBalance,
    buildQuests,
    setItems,
    setPoints,
    setError,
    setLoading,
  ]);

  useEffect(() => {
    setRefetch(refetch);
  }, [refetch, setRefetch]);

  // On chain change: clear local pending & auto-claim flags and refetch fresh
  useEffect(() => {
    setTxPending(null);
    try {
      localStorage.removeItem(PENDING_KEY);
      localStorage.removeItem(autoRequestKey(chainId, address));
    } catch {}
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId]);

  const ready = Boolean(publicClient && address);

  // fire once when publicClient/address become ready
  useEffect(() => {
    if (ready) refetch();
  }, [ready]);

  useEffect(() => {
    if (ready && questsData) refetch();
  }, [questsData, ready]);

  useEffect(() => {
    refetch();
  }, [refetch, chainId]);

  useEffect(() => {
    let cancelled = false;

    async function fetchPoolOnce() {
      if (!publicClient || !address) return;
      try {
        const { poolCents, decimals } = await getPoolBalance(publicClient);
        const dollars = formatUsd(Number(poolCents), decimals);
        const currentItems = items;
        const { next, changed } = withPrizeCratePoints(currentItems, Number(dollars));
        if (!cancelled && changed) {
          setItems(next);
        }
      } catch (err) {
        console.error('fetchPool error:', err);
      }
    }

    fetchPoolOnce();

    return () => {
      cancelled = true;
    };
  }, [publicClient]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!localStorage) {
      console.warn('localStorage is not available');
      return;
    }

    const saved = localStorage.getItem(PENDING_KEY);
    if (saved) {
      const [id, ts] = saved.split(':');
      const startedAt = Number(ts);
      if (!isNaN(startedAt) && Date.now() - startedAt < PENDING_TIMEOUT) {
        setTxPending(id);
        refetch();
      } else {
        localStorage.removeItem(PENDING_KEY);
      }
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (txPending) {
      localStorage.setItem(PENDING_KEY, `${txPending}:${Date.now()}`);
    } else {
      localStorage.removeItem(PENDING_KEY);
    }
  }, [txPending]);

  const claim = useCallback(
    async (id: string): Promise<boolean> => {
      if (!publicClient || !walletClient || !address) return false;
      if (id !== QuestTypes.Daily) return false;

      setTxPending(id);

      try {
        const tx = (await writeRunDailyQuest(publicClient, walletClient, address)) as
          | string
          | { hash: string }
          | undefined;

        const hash = typeof tx === 'string' ? tx : tx && 'hash' in tx ? tx.hash : undefined;
        if (hash) {
          await publicClient.waitForTransactionReceipt({
            hash: hash as `0x${string}`,
            confirmations: 2,
          });
        }
        localStorage.setItem(autoRequestKey(chainId, address), 'claimed');

        await refetch();
        return true;
      } catch (e) {
        console.error(e);
        setError('Failed to claim Daily Log in');
        return false;
      } finally {
        setTxPending(null);
      }
    },
    [publicClient, walletClient, address, refetch, setError],
  );

  useEffect(() => {
    setClaim(claim);
  }, [claim, setClaim]);

  const derived = useMemo(() => {
    let claimable = 0;
    let activeDaily = 0;
    for (const q of items) {
      if (q.status === NextTimeStatus.Ready) {
        if (q.id === QuestTypes.Daily) claimable += 1;
        if (q.frequency === QuestPeriod.Daily) activeDaily += 1;
      }
    }
    return {
      claimable,
      activeDaily,
      txPending: txPending ?? '',
      pointBalance,
    };
  }, [items, txPending, pointBalance]);

  useEffect(() => {
    setActiveQuests(derived.activeDaily);
    setTotals({
      claimable: derived.claimable,
      txPending: derived.txPending,
      bonusPoints: Number(derived.pointBalance),
    });
  }, [derived, setActiveQuests, setTotals]);

  // Auto-clear transient errors
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error, setError]);

  return {
    items,
    loading,
    error,
    claim,
    totals: {
      claimable: derived.claimable,
      txPending: derived.txPending,
      bonusPoints: Number(derived.pointBalance),
    },
    refresh: refetch,
  };
}
